import { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useSearchParams,
} from 'react-router-dom';

import { HomePage } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-home-page';
import { IssueList } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list';
import { IssueSingleView } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-single-view';
import { ProjectRedirect } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-project-redirect';
import { TosFooter } from '@mojira/tos-footer/app/footer';
import {
  BackendAdapterProvider,
  InvokePayload,
} from '@eng-mono/atlassian/shared/frontend-ui-backend-adapter';
import { ClickedLinkProvider } from '@eng-mono/atlassian/shared/frontend-ui-contexts';

interface Config {
  localServer: { url: string; port: string };
  showAdminPage: boolean;
  appName: string;
}

export interface AppProps {
  config: Config;
}

interface FilterMapping {
  newId: string;
  description: string;
  discoveredDate?: string;
}

const LEGACY_FILTER_MAPPING: Record<string, FilterMapping> = {
  // Pre-existing bugs for 1.21
  '28428': {
    newId: '10242',
    description: 'Pre-existing bugs for 1.21',
    discoveredDate: '2025-01-15',
  },
  // Template:
  // 'oldFilterId': {
  //   newId: 'newFilterId',
  //   description: 'Filter description',
  //   discoveredDate: 'YYYY-MM-DD'
  // },
};

// Legacy filter redirect component for /issues/?filter=X URLs
function LegacyIssueRedirect() {
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get('filter');

  if (!filterId) {
    return <Navigate to="/browse/MC?type=all" replace />;
  }

  const mapping = LEGACY_FILTER_MAPPING[filterId];

  if (mapping) {
    const targetUrl = `/browse/MC?filter=${mapping.newId}&type=all`;
    return <Navigate to={targetUrl} replace />;
  } else {
    const targetUrl = `/browse/MC?type=all`;
    return <Navigate to={targetUrl} replace />;
  }
}

function App({ config }: AppProps) {
  const [siteUrl, setSiteUrl] = useState<string | null>(null);

  useEffect(() => {
    const base =
      config.localServer?.url +
      (config.localServer?.port ? ':' + config.localServer.port : '');

    fetch(base.endsWith('/') ? base + 'api/site-info' : base + '/api/site-info')
      .then((response) => response.json())
      .then((data: { siteUrl: string }) => {
        return data.siteUrl;
      })
      .then((siteUrl) => {
        setSiteUrl(siteUrl);
      });
  }, [config.localServer?.url, config.localServer?.port]);

  const invoke = async <T = unknown, U = InvokePayload>(
    action: string,
    data?: U
  ) => {
    const workspaceId =
      localStorage.getItem('mojira-bugtracker-user-workspace-id') ?? '';
    return fetch(
      config.localServer?.url +
        (config.localServer?.port ? ':' + config.localServer.port : '') +
        '/api/' +
        action,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'resource-id':
            localStorage.getItem('mojira-bugtracker-user-workspace-id') ?? '',
        },
        body: JSON.stringify(data ? { ...data, workspaceId } : { workspaceId }),
      }
    ).then(async (response) => {
      const data = await response.json();
      if (response.ok) {
        return data as T;
      } else {
        throw new Error(`${response.statusText} - ${data.message}`);
      }
    });
  };

  return (
    <>
      <BackendAdapterProvider invokeImplementation={invoke}>
        {siteUrl ? (
          <Router>
            <ClickedLinkProvider>
              <Routes>
                <Route path="/" element={<HomePage siteUrl={siteUrl} />} />
                <Route path="/issues" element={<LegacyIssueRedirect />} />
                <Route
                  path="browse/:project"
                  element={<IssueList pageSize={25} siteUrl={siteUrl} />}
                />
                <Route
                  path="browse/:project/issues/:issueKey"
                  element={<IssueSingleView siteUrl={siteUrl} />}
                />
                <Route
                  path="projects/:project/summary"
                  element={<ProjectRedirect />}
                />
                <Route path="*" element={<Navigate to="/" replace={true} />} />
              </Routes>
            </ClickedLinkProvider>
          </Router>
        ) : (
          <div>Loading...</div>
        )}
      </BackendAdapterProvider>

      <TosFooter />
    </>
  );
}

export default App;
